import { Dayjs } from 'dayjs'
import { memo, PropsWithChildren } from 'react'

import { AstraCallout, AstraHoverCard, AstraParagraph, AstraTooltip, astraTwMerge, format } from '~/astra'
import { isSelectionForScheduleDate } from '~/store/slices/selectionDrawerSlice'
import { useCommentCountBySelection } from '~/storm'

import { CommentIndicator } from './Commenting/CommentIndicator'
import { CellCommentPopover } from './Commenting/CommentPopover'
import { useSelectionDrawer } from './Drawer/SelectionDrawer/useSelectionDrawer'
import { Show } from './Show'

type SharedProps = {
    date: Dayjs
    isHoliday: boolean
    warning?: string
    fullHeight?: boolean
}

type DayFormatForColumnProps = SharedProps

type DayColumnProps = PropsWithChildren<SharedProps>

const DayFormatForColumn = ({ date, isHoliday, warning }: DayFormatForColumnProps) => {
    const label = format(date, 'dd DD')
    const dayDateSplit = label.split(' ')
    const dayTextStyle = isHoliday ? 'text-critical' : 'text-muted'

    return (
        <div className="flex">
            <div className="calendar-date flex w-full items-center justify-center gap-x-1 whitespace-nowrap capitalize">
                <Show condition={!!warning}>
                    <div className="h-full w-[3px] shrink-0 rounded-full bg-caution-strong-rest" />
                </Show>
                <AstraParagraph className={`day ${dayTextStyle} select-none`}>{dayDateSplit[0]}</AstraParagraph>
                <div className="date flex select-none items-center justify-center rounded">
                    <AstraParagraph className={dayTextStyle}>{dayDateSplit[1]}</AstraParagraph>
                </div>
            </div>
        </div>
    )
}

export const DayColumn = memo(({ date, isHoliday, warning, children, fullHeight = true }: DayColumnProps) => (
    <div className={`flex w-full flex-col gap-1 ${fullHeight ? 'h-full' : 'h-fit'}`}>
        {isHoliday ? (
            <AstraTooltip tooltipContent="Helligdag" side="bottom">
                <div className="flex flex-col">
                    <DayFormatForColumn date={date} isHoliday />
                    {children}
                </div>
            </AstraTooltip>
        ) : warning ? (
            <AstraHoverCard
                showArrow={false}
                noPadding
                placement="bottom"
                sideOffset={15}
                hoverContent={<AstraCallout color="caution" text={warning} size="small" />}
            >
                <div className="flex flex-col">
                    <DayFormatForColumn date={date} isHoliday={false} warning={warning} />
                    {children}
                </div>
            </AstraHoverCard>
        ) : (
            <div className="flex flex-col">
                <DayFormatForColumn date={date} isHoliday={false} />
                {children}
            </div>
        )}
    </div>
))

type DayColumnWithCommentIndicatorProps = DayColumnProps & {
    commentCount: number
    isSelected: boolean
    onClick?: () => void
}

export const DayColumnWithCommentIndicator = memo(({ commentCount, isSelected, onClick, ...dayColumnProps }: DayColumnWithCommentIndicatorProps) => (
    <div
        className={astraTwMerge(
            'relative w-full',
            isSelected ? 'outline outline-offset-[3px] outline-[#126df6]' : '',
            dayColumnProps.fullHeight ? 'h-full' : 'h-fit',
            onClick ? 'cursor-pointer' : ''
        )}
        onClick={onClick}
    >
        <DayColumn {...dayColumnProps} />
        {onClick && commentCount > 0 && (
            <div className="absolute right-0 top-0 w-full">
                <CellCommentPopover date={dayColumnProps.date} commentCount={commentCount} onOpenInSidebar={onClick}>
                    <div className="absolute right-[-3px] top-[-3px] @[125px]:hidden">
                        <svg
                            role="presentation"
                            width="12"
                            height="12"
                            viewBox="0 0 8 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ color: 'rgb(172 216 252)' }}
                        >
                            <path d="M8 0 L8 8 L0 0 Z" fill="currentColor" />
                        </svg>
                    </div>
                    <div className="hidden @[125px]:block">
                        <CommentIndicator commentCount={commentCount} />
                    </div>
                </CellCommentPopover>
            </div>
        )}
    </div>
))

export const DayColumnWithComment = ({ date, ...commentIndicatorProps }: DayColumnProps) => {
    const commentCount = useCommentCountBySelection({
        type: 'scheduleDate',
        date,
    })
    const {
        selection,
        actions: { closeDrawer },
        openDateSelectionWithPageDrawer,
    } = useSelectionDrawer()
    const isSelected = isSelectionForScheduleDate(selection, date)

    const handleClick = () => {
        if (isSelected) {
            closeDrawer()
        } else {
            openDateSelectionWithPageDrawer(date)
        }
    }
    return <DayColumnWithCommentIndicator date={date} {...commentIndicatorProps} commentCount={commentCount} onClick={handleClick} isSelected={isSelected} />
}
